import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  IconButton,
  TextField,
  Chip,
  CircularProgress,
  Alert,
  useTheme,
  alpha,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  AutoAwesome as AIIcon,
  Image as ImageIcon,
  Upload as UploadIcon,
  Search as SearchIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  FolderOpen as FolderIcon,
  CloudUpload as CloudUploadIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import UserDocumentsModal from './Documents/UserDocumentsModal';
import AIDocumentProcessingModal from './Documents/AIDocumentProcessingModal';

interface AISearchWithImageModalProps {
  open: boolean;
  onClose: () => void;
  onSearch: (imageFile: File, searchQuery?: string) => void;
}

// Beautiful Spinner Component
const BeautifulSpinner: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <Box sx={{ position: 'relative', display: 'inline-flex' }}>
    <motion.div
      animate={{ rotate: 360 }}
      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
      style={{ width: size, height: size }}
    >
      <RefreshIcon sx={{ fontSize: size, color: 'white' }} />
    </motion.div>
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        borderRadius: '50%',
        border: `2px solid transparent`,
        borderTop: `2px solid rgba(255, 255, 255, 0.3)`,
        animation: 'spin 1s linear infinite',
        '@keyframes spin': {
          '0%': { transform: 'rotate(0deg)' },
          '100%': { transform: 'rotate(360deg)' },
        },
      }}
    />
  </Box>
);

const AISearchWithImageModal: React.FC<AISearchWithImageModalProps> = ({
  open,
  onClose,
  onSearch,
}) => {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [documentsModalOpen, setDocumentsModalOpen] = useState(false);
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiProcessingDocument, setAiProcessingDocument] = useState<any>(null);

  // Debug: Log when AI modal state changes
  useEffect(() => {
    console.log('🤖 AI Modal state changed:', { aiModalOpen, hasDocument: !!aiProcessingDocument });
  }, [aiModalOpen, aiProcessingDocument]);

  const handleFileSelect = (file: File) => {
    if (file && file.type.startsWith('image/')) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      toast.error('Please select a valid image file');
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
  };

  // Convert File to UserDocument format for AI processing
  const createUserDocumentFromFile = (file: File): any => {
    // Create a proper URL for the file that can be accessed by the API
    const fileUrl = URL.createObjectURL(file);
    
    return {
      id: `temp-${Date.now()}`,
      file_name: file.name,
      file_type: file.type,
      file_size: file.size,
      url: fileUrl, // Use the object URL instead of data URL
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      user_id: 'temp-user',
      company_id: null,
      is_public: false,
      tags: [],
      document_type: 'other',
      description: null,
      metadata: null,
    };
  };

  const handleSearch = async () => {
    if (!selectedImage) {
      toast.error('Please select an image first');
      return;
    }

    console.log('🔍 Starting AI search process...');
    
    // Create a UserDocument from the selected image
    const userDocument = createUserDocumentFromFile(selectedImage);
    console.log('📄 Created user document:', userDocument);
    
    // Set the document for AI processing
    setAiProcessingDocument(userDocument);
    
    // Open AI processing modal - don't close search modal yet
    console.log('🔄 Opening AI processing modal...');
    setAiModalOpen(true);
    
    toast.success('Opening AI analysis...');
  };

  const handleDocumentSelect = (document: any) => {
    // Convert document to File object for processing
    if (document.url) {
      fetch(document.url)
        .then(response => response.blob())
        .then(blob => {
          const file = new File([blob], document.file_name, { type: blob.type });
          handleFileSelect(file);
          setDocumentsModalOpen(false);
          toast.success('Image selected from documents!');
        })
        .catch(error => {
          console.error('Error loading document:', error);
          toast.error('Failed to load image from documents');
        });
    }
  };

  const handleClose = () => {
    // Clean up object URLs to prevent memory leaks
    if (aiProcessingDocument?.url && aiProcessingDocument.url.startsWith('blob:')) {
      URL.revokeObjectURL(aiProcessingDocument.url);
    }
    
    setSelectedImage(null);
    setImagePreview(null);
    setSearchQuery('');
    setIsProcessing(false);
    setAiModalOpen(false);
    setAiProcessingDocument(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
        },
      }}
    >
      <DialogTitle sx={{ 
        pb: 1,
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        borderRadius: '12px 12px 0 0',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(10px)',
          }}>
            <AIIcon sx={{ fontSize: 24 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
              🔍 AI Search with Image
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Upload an image to find similar products using AI
            </Typography>
          </Box>
          <IconButton onClick={handleClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 4, position: 'relative' }}>
        {/* Hidden File Input - Always Available */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileInput}
          style={{ display: 'none' }}
        />
        
        {/* Processing Overlay */}
        <AnimatePresence>
          {isProcessing && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(8px)',
                zIndex: 1000,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '12px',
              }}
            >
              <motion.div
                animate={{ 
                  scale: [1, 1.1, 1],
                  rotate: [0, 360]
                }}
                transition={{ 
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }}
                style={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 24,
                  boxShadow: '0 8px 32px rgba(139, 92, 246, 0.3)',
                }}
              >
                <AIIcon sx={{ fontSize: 40, color: 'white' }} />
              </motion.div>
              
              <Typography variant="h5" sx={{ 
                fontWeight: 700, 
                color: '#1e293b',
                mb: 1,
                textAlign: 'center'
              }}>
                🤖 AI Analysis in Progress
              </Typography>
              
              <Typography variant="body1" sx={{ 
                color: '#64748b',
                textAlign: 'center',
                maxWidth: 400,
                mb: 3
              }}>
                Our AI is analyzing your image to identify visual elements, 
                colors, patterns, and objects...
              </Typography>
              
              <Box sx={{ 
                width: 200, 
                height: 4, 
                background: '#e2e8f0',
                borderRadius: 2,
                overflow: 'hidden',
                mb: 2
              }}>
                <motion.div
                  animate={{ x: ['-100%', '100%'] }}
                  transition={{ 
                    duration: 1.5,
                    repeat: Infinity,
                    ease: 'easeInOut'
                  }}
                  style={{
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, #8b5cf6, #a855f7, #8b5cf6)',
                    borderRadius: 2,
                  }}
                />
              </Box>
              
              <Typography variant="caption" sx={{ 
                color: '#94a3b8',
                textAlign: 'center'
              }}>
                This may take a few moments...
              </Typography>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {!selectedImage ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3 }}
            >
              {/* Upload Area */}
              <Paper
                sx={{
                  p: 4,
                  textAlign: 'center',
                  border: `2px dashed ${dragActive ? theme.palette.primary.main : '#cbd5e1'}`,
                  borderRadius: 3,
                  background: dragActive 
                    ? alpha(theme.palette.primary.main, 0.05)
                    : 'rgba(255, 255, 255, 0.8)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    background: alpha(theme.palette.primary.main, 0.05),
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 20px rgba(0, 0, 0, 0.1)',
                  },
                }}
                onClick={() => fileInputRef.current?.click()}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                <motion.div
                  animate={{ scale: dragActive ? 1.1 : 1 }}
                  transition={{ duration: 0.2 }}
                >
                  <ImageIcon sx={{ 
                    fontSize: 64, 
                    color: dragActive ? theme.palette.primary.main : '#94a3b8',
                    mb: 2 
                  }} />
                </motion.div>
                
                <Typography variant="h6" sx={{ 
                  fontWeight: 600, 
                  color: '#1e293b',
                  mb: 1 
                }}>
                  {dragActive ? 'Drop your image here' : 'Upload an Image'}
                </Typography>
                
                <Typography variant="body2" sx={{ 
                  color: '#64748b',
                  mb: 2 
                }}>
                  Drag and drop an image or click to browse
                </Typography>
                
                <Chip 
                  label="JPG, PNG, GIF, WebP" 
                  size="small" 
                  variant="outlined"
                  sx={{ 
                    background: alpha(theme.palette.primary.main, 0.1),
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                  }}
                />
              </Paper>
              
              {/* Choose from Documents Button */}
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Divider sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ color: '#64748b', px: 2 }}>
                    OR
                  </Typography>
                </Divider>
                
                <Button
                  variant="outlined"
                  startIcon={<FolderIcon />}
                  onClick={() => setDocumentsModalOpen(true)}
                  sx={{
                    borderColor: '#8b5cf6',
                    color: '#8b5cf6',
                    borderRadius: 2,
                    px: 3,
                    py: 1,
                    fontWeight: 600,
                    textTransform: 'none',
                    background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.05) 0%, rgba(139, 92, 246, 0.02) 100%)',
                    '&:hover': {
                      borderColor: '#7c3aed',
                      backgroundColor: 'rgba(139, 92, 246, 0.1)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(139, 92, 246, 0.2)',
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  Choose from Documents
                </Button>
                
                <Typography variant="caption" sx={{ 
                  color: '#64748b',
                  mt: 1,
                  display: 'block',
                }}>
                  Select an image from your uploaded documents
                </Typography>
              </Box>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Image Preview and Search */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ 
                  fontWeight: 600, 
                  mb: 2,
                  color: '#1e293b',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}>
                  <CheckIcon sx={{ color: theme.palette.success.main }} />
                  Image Selected
                </Typography>
                
                <Paper sx={{ 
                  p: 2, 
                  borderRadius: 3,
                  background: 'rgba(255, 255, 255, 0.9)',
                  border: '1px solid #e2e8f0',
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 3,
                    alignItems: 'flex-start',
                  }}>
                    <Box sx={{
                      width: 120,
                      height: 120,
                      borderRadius: 2,
                      overflow: 'hidden',
                      border: '2px solid #e2e8f0',
                    }}>
                      <img
                        src={imagePreview || ''}
                        alt="Preview"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    </Box>
                    
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ 
                        color: '#64748b',
                        mb: 1,
                      }}>
                        File: {selectedImage?.name}
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        color: '#64748b',
                        mb: 2,
                      }}>
                        Size: {(selectedImage?.size / 1024 / 1024).toFixed(2)} MB
                      </Typography>
                      
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<UploadIcon />}
                          onClick={() => fileInputRef.current?.click()}
                          sx={{
                            borderColor: theme.palette.primary.main,
                            color: theme.palette.primary.main,
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            },
                          }}
                        >
                          Upload New
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<FolderIcon />}
                          onClick={() => setDocumentsModalOpen(true)}
                          sx={{
                            borderColor: '#8b5cf6',
                            color: '#8b5cf6',
                            '&:hover': {
                              backgroundColor: alpha('#8b5cf6', 0.1),
                            },
                          }}
                        >
                          From Documents
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              </Box>

              {/* Search Query */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ 
                  fontWeight: 600, 
                  mb: 2,
                  color: '#1e293b',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}>
                  <SearchIcon sx={{ color: theme.palette.primary.main }} />
                  Additional Search Terms (Optional)
                </Typography>
                
                <TextField
                  fullWidth
                  placeholder="e.g., 'red dress', 'vintage style', 'summer collection'"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      background: 'rgba(255, 255, 255, 0.9)',
                      '& fieldset': {
                        borderColor: '#e2e8f0',
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                        borderWidth: 2,
                      },
                    },
                  }}
                />
                <Typography variant="caption" sx={{ 
                  color: '#64748b',
                  mt: 1,
                  display: 'block',
                }}>
                  Add specific keywords to refine your search results
                </Typography>
              </Box>

              {/* AI Processing Info */}
              <Alert 
                severity="info" 
                sx={{ 
                  borderRadius: 2,
                  background: alpha(theme.palette.info.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                }}
              >
                <Typography variant="body2">
                  <strong>🤖 AI Analysis:</strong> Our AI will analyze your image to identify visual elements, 
                  colors, patterns, and objects to find the most similar products in our catalog.
                </Typography>
              </Alert>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          onClick={handleClose}
          sx={{
            color: '#64748b',
            '&:hover': {
              backgroundColor: alpha('#64748b', 0.1),
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSearch}
          disabled={!selectedImage || isProcessing}
          variant="contained"
          startIcon={isProcessing ? <BeautifulSpinner size={20} /> : <AIIcon />}
          sx={{
            background: isProcessing 
              ? 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)'
              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: 2,
            px: 3,
            py: 1,
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: isProcessing 
              ? '0 4px 12px rgba(139, 92, 246, 0.4)'
              : '0 4px 12px rgba(102, 126, 234, 0.3)',
            '&:hover': {
              background: isProcessing
                ? 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)'
                : 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
              boxShadow: isProcessing
                ? '0 4px 12px rgba(139, 92, 246, 0.4)'
                : '0 6px 16px rgba(102, 126, 234, 0.4)',
              transform: isProcessing ? 'none' : 'translateY(-2px)',
            },
            '&:disabled': {
              background: '#cbd5e1',
              color: '#94a3b8',
              transform: 'none',
              boxShadow: 'none',
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          {isProcessing ? 'Analyzing Image...' : 'Find Similar Products'}
        </Button>
      </DialogActions>

      {/* User Documents Modal */}
      <UserDocumentsModal
        open={documentsModalOpen}
        onClose={() => setDocumentsModalOpen(false)}
        onDocumentSelect={handleDocumentSelect}
        mode="select"
        imagesOnly={true}
      />

      {/* AI Document Processing Modal */}
      <AIDocumentProcessingModal
        open={aiModalOpen}
        onClose={() => {
          // Clean up object URLs to prevent memory leaks
          if (aiProcessingDocument?.url && aiProcessingDocument.url.startsWith('blob:')) {
            URL.revokeObjectURL(aiProcessingDocument.url);
          }
          setAiModalOpen(false);
          setAiProcessingDocument(null);
          // Close the search modal when AI processing modal closes
          onClose();
        }}
        document={aiProcessingDocument}
        autoProcessImageTags={true}
      />
    </Dialog>
  );
};

export default AISearchWithImageModal;
